import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  FiSearch, FiShield, FiCheckCircle, FiLock, FiArrowRight, 
  FiCheck, FiAlertCircle, FiUser, FiBriefcase, FiShieldOff,
  FiThumbsUp, FiClock, FiCreditCard, FiPercent, FiTrendingUp,
  FiDollarSign, FiCheckSquare, FiArrowDown, FiMenu, FiX, FiActivity
} from 'react-icons/fi';
import { consultCPF, consultCNPJ } from '../services/api';

// Função para validar CPF com verificação de números sequenciais
const validateCPF = (cpf) => {
  const cleanCpf = cpf.replace(/\D/g, '');
  
  if (cleanCpf.length !== 11) {
    return { valid: false, error: 'CPF deve ter 11 dígitos' };
  }
  
  if (/^(\d)\1{10}$/.test(cleanCpf)) {
    return { valid: false, error: 'CPF inválido' };
  }
  
  const sequential = [
    '12345678901', '98765432109', '11122233344', '22233344455',
    '33344455566', '44455566677', '55566677788', '66677788899', '00000000000'
  ];
  
  if (sequential.includes(cleanCpf)) {
    return { valid: false, error: 'CPF inválido' };
  }
  
  let sum = 0;
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanCpf.substring(i - 1, i)) * (11 - i);
  }
  
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(9, 10))) {
    return { valid: false, error: 'CPF inválido' };
  }
  
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanCpf.substring(i - 1, i)) * (12 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(10, 11))) {
    return { valid: false, error: 'CPF inválido' };
  }
  
  return { valid: true };
};

// Função para validar CNPJ
const validateCNPJ = (cnpj) => {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  
  if (cleanCNPJ.length !== 14) {
    return { valid: false, error: 'CNPJ deve ter 14 dígitos' };
  }
  
  if (/^(\d)\1{13}$/.test(cleanCNPJ)) {
    return { valid: false, error: 'CNPJ inválido' };
  }
  
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight--;
    if (weight === 1) weight = 9;
  }
  
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (digit1 !== parseInt(cleanCNPJ.charAt(12))) {
    return { valid: false, error: 'CNPJ inválido' };
  }
  
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight--;
    if (weight === 1) weight = 9;
  }
  
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  
  if (digit2 !== parseInt(cleanCNPJ.charAt(13))) {
    return { valid: false, error: 'CNPJ inválido' };
  }
  
  return { valid: true };
};

// Componente de Toast para mensagens
const Toast = ({ message, type, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          className={`fixed top-24 left-1/2 -translate-x-1/2 z-50 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 ${
            type === 'error' 
              ? 'bg-red-50 border-2 border-red-200 text-red-800' 
              : 'bg-green-50 border-2 border-green-200 text-green-800'
          }`}
        >
          {type === 'error' ? (
            <FiAlertCircle className="w-6 h-6 text-red-500" />
          ) : (
            <FiCheckCircle className="w-6 h-6 text-green-500" />
          )}
          <span className="font-medium">{message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Componente do Toggle CPF/CNPJ Premium
const DocumentToggle = ({ type, onChange }) => {
  return (
    <div className="flex bg-gray-100 p-1.5 rounded-xl">
      <button
        type="button"
        onClick={() => onChange('CPF')}
        className={`flex-1 py-3 px-5 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
          type === 'CPF'
            ? 'bg-white text-blue-600 shadow-lg transform scale-105'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <FiUser className="w-4 h-4" />
        CPF
      </button>
      <button
        type="button"
        onClick={() => onChange('CNPJ')}
        className={`flex-1 py-3 px-5 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
          type === 'CNPJ'
            ? 'bg-white text-blue-600 shadow-lg transform scale-105'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <FiBriefcase className="w-4 h-4" />
        CNPJ
      </button>
    </div>
  );
};

// Componente do Card de Consulta Premium
const ConsultationCard = ({ document, documentType, loading, loadingStep, onDocumentChange, onTypeChange, onSubmit, error }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100 relative overflow-hidden"
    >
      {/* Glassmorphism effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-blue-50/30 pointer-events-none" />
      
      <div className="relative z-10">
        {/* Icone de destaque */}
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-500/30">
          <FiSearch className="w-8 h-8 text-white" />
        </div>
        
        <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
          Consultar {documentType}
        </h3>
        <p className="text-gray-500 text-center text-sm mb-6">
          Digite abaixo e veja suas pendências
        </p>
        
        <DocumentToggle type={documentType} onChange={onTypeChange} />
        
        <div className="mt-5">
          <input
            type="text"
            value={document}
            onChange={onDocumentChange}
            onKeyDown={(e) => e.key === 'Enter' && !loading && document.replace(/\D/g, '').length >= (documentType === 'CPF' ? 11 : 14) && onSubmit(e)}
            placeholder={documentType === 'CPF' ? '000.000.000-00' : '000.000.000/0000-00'}
            className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-100 text-gray-900 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none text-lg font-mono text-center"
            maxLength={documentType === 'CPF' ? 14 : 18}
          />
        </div>
        
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-3 flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-xl"
          >
            <FiAlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </motion.div>
        )}
        
        <button
          onClick={onSubmit}
          disabled={loading || document.replace(/\D/g, '').length < (documentType === 'CPF' ? 11 : 14)}
          className="w-full mt-5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{loadingStep || 'Consultando...'}</span>
            </>
          ) : (
            <>
              <span>Consultar agora</span>
              <FiArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
        
        <p className="text-center text-xs text-gray-400 mt-4">
          🔒 Seus dados são criptografados
        </p>
      </div>
    </motion.div>
  );
};

// Seção Como Funciona
const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: <FiUser className="w-8 h-8" />,
      title: 'Digite seu CPF',
      description: 'Informe seu documento abaixo e nossa IA busca automaticamente no bancos de dados',
    },
    {
      number: '02',
      icon: <FiSearch className="w-8 h-8" />,
      title: 'Veja suas dívidas',
      description: 'Em segundos, visualize todas as pendências e débitos registrados em seu nome',
    },
    {
      number: '03',
      icon: <FiPercent className="w-8 h-8" />,
      title: 'Negocie com desconto',
      description: 'Escolha a melhor opção de pagamento e limpe seu nome com até 90% de desconto',
    },
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
            Como funciona
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2">
            Em 3 passos simples
          </h2>
          <p className="text-xl text-gray-600 mt-4 max-w-2xl mx-auto">
            Consulta rápida, segura e sem burocracia
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {step.number}
              </div>
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-blue-600 mt-4">
                {step.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-3">{step.title}</h3>
              <p className="text-gray-600 text-center">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Seção de Confiança
const TrustSection = () => {
  const stats = [
    { value: '+10.000', label: 'Consultas realizadas' },
    { value: '98%', label: 'Satisfação dos clientes' },
    { value: '100%', label: 'Dados protegidos (LGPD)' },
    { value: '24/7', label: 'Disponível sempre' },
  ];

  const features = [
    { icon: <FiLock className="w-6 h-6" />, title: 'Criptografia SSL', description: 'Seus dados protegidos com padrão bancário' },
    { icon: <FiShield className="w-6 h-6" />, title: 'LGPD Compliant', description: 'Total conformidade com a lei de proteção de dados' },
    { icon: <FiThumbsUp className="w-6 h-6" />, title: 'Sem Spam', description: 'Não compartilhamos seus dados com terceiros' },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
              <div className="text-gray-600 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-4 p-6 bg-gray-50 rounded-2xl"
            >
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">{feature.title}</h4>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Seção de Conversão / Ofertas
const ConversionSection = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
        <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
            <FiTrendingUp className="w-5 h-5 text-white" />
            <span className="text-white font-medium text-sm">Oferta especial</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Limpe seu nome com até <span className="text-yellow-300">90% de desconto</span>
          </h2>
          
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Não deixe as dívidas negatively impactarem sua vida. Negocie agora mesmo e recupere sua liberdade financeira.
          </p>

          <button 
            onClick={scrollToTop}
            className="bg-white text-blue-600 font-bold py-4 px-10 rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3 mx-auto"
          >
            <FiDollarSign className="w-6 h-6" />
            Ver ofertas
            <FiArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Simples
const Footer = () => {
  return (
    <footer className="bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center">
              <FiShield className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-lg">ConsulteSeuNome</span>
          </div>
          
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <a href="#" className="hover:text-white transition-colors">Termos de uso</a>
            <a href="#" className="hover:text-white transition-colors">Privacidade</a>
            <a href="#" className="hover:text-white transition-colors">Contato</a>
          </div>
          
          <p className="text-gray-500 text-sm">
            © 2026 ConsulteSeuNome. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Componente Principal
const Home = () => {
  const [document, setDocument] = useState('');
  const [documentType, setDocumentType] = useState('CPF');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const formatDocument = (value, type) => {
    const cleaned = value.replace(/\D/g, '');
    if (type === 'CPF') {
      const formatted = cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      return formatted.slice(0, 14);
    } else {
      const formatted = cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      return formatted.slice(0, 18);
    }
  };

  const handleDocumentTypeChange = (type) => {
    setDocumentType(type);
    setDocument('');
    setError('');
  };

  const handleDocumentChange = (e) => {
    const value = e.target.value;
    setDocument(formatDocument(value, documentType));
    setError('');
  };

  const showToast = (message, type) => {
    setToast({ message, type, visible: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cleanDocument = document.replace(/\D/g, '');
    
    let validation;
    if (documentType === 'CPF') {
      validation = validateCPF(cleanDocument);
      if (!validation.valid) {
        setError(validation.error || 'Por favor, digite um CPF válido');
        showToast(validation.error || 'CPF inválido', 'error');
        return;
      }
    } else {
      validation = validateCNPJ(cleanDocument);
      if (!validation.valid) {
        setError(validation.error || 'Por favor, digite um CNPJ válido');
        showToast(validation.error || 'CNPJ inválido', 'error');
        return;
      }
    }

    setLoading(true);
    setLoadingStep('Buscando dados...');
    
    // Simular passos de carregamento para melhor feedback visual
    const steps = ['Validando...', 'Consultando bureau...', 'Buscando pendências...'];
    let stepIdx = 0;
    const stepInterval = setInterval(() => {
      if (stepIdx < steps.length) {
        setLoadingStep(steps[stepIdx]);
        stepIdx++;
      } else {
        clearInterval(stepInterval);
      }
    }, 1500);

    try {
      let response;
      if (documentType === 'CPF') {
        response = await consultCPF(cleanDocument);
      } else {
        response = await consultCNPJ(cleanDocument);
      }
      
      clearInterval(stepInterval);
      setLoadingStep('Finalizando...');
      
      showToast('Consulta realizada com sucesso!', 'success');
      
      sessionStorage.setItem('consultResult', JSON.stringify({
        document: cleanDocument,
        documentType: documentType,
        ...response.data
      }));
      
      setTimeout(() => {
        navigate('/resultado');
      }, 500);
    } catch (err) {
      console.error('Erro na consulta:', err);
      const errorMessage = err.response?.data?.error || `Erro ao consultar ${documentType}. Tente novamente.`;
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ConsulteSeuNome - Limpe seu nome em minutos</title>
        <meta name="description" content="Descubra se seu nome está negativado e veja como limpar agora. Consulta rápida, segura e gratuita." />
      </Helmet>

      <Toast 
        message={toast.message} 
        type={toast.type} 
        isVisible={toast.visible} 
        onClose={() => setToast({ ...toast, visible: false })} 
      />

      {/* Header Premium */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-lg shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <FiShield className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">
                ConsulteSeu<span className="text-blue-600">Nome</span>
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#inicio" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Início</a>
              <a href="#como-funciona" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Como funciona</a>
              <a href="#seguranca" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Segurança</a>
              <a href="/login" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-2.5 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-blue-500/30">
                Entrar
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t"
            >
              <div className="px-4 py-4 space-y-4">
                <a href="#inicio" className="block text-gray-700 hover:text-blue-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Início</a>
                <a href="#como-funciona" className="block text-gray-700 hover:text-blue-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Como funciona</a>
                <a href="#seguranca" className="block text-gray-700 hover:text-blue-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Segurança</a>
                <a href="/login" className="block bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 px-6 rounded-xl text-center" onClick={() => setMobileMenuOpen(false)}>
                  Entrar
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Hero Section with 2 Columns */}
      <section id="inicio" className="relative pt-32 pb-20 bg-gradient-to-br from-blue-50 via-white to-blue-100 overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-pattern opacity-30" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '1s'}} />
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-6">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-blue-700 font-medium text-sm">100% gratuito</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Consulte seu CPF e{' '}
                <span className="text-blue-600">limpe seu nome</span>{' '}
                hoje
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Veja suas dívidas, negocie com desconto e recupere seu crédito em minutos.
              </p>

              {/* Bullet points */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: <FiCheckCircle className="w-6 h-6 text-green-500" />, text: 'Consulta gratuita' },
                  { icon: <FiClock className="w-6 h-6 text-green-500" />, text: 'Resultado imediato' },
                  { icon: <FiShield className="w-6 h-6 text-green-500" />, text: '100% seguro' },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    {item.icon}
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-3">
                  <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=64&h=64&fit=crop&crop=faces" alt="Usuário 1" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=faces" alt="Usuário 2" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=64&h=64&fit=crop&crop=faces" alt="Usuário 3" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                </div>
                <span className="text-gray-600 text-sm">
                  <span className="font-semibold text-gray-900">+10.000</span> pessoas já consultaram
                </span>
              </div>
            </motion.div>

            {/* Right Column - Consultation Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ConsultationCard
                document={document}
                documentType={documentType}
                loading={loading}
                loadingStep={loadingStep}
                onDocumentChange={handleDocumentChange}
                onTypeChange={handleDocumentTypeChange}
                onSubmit={handleSubmit}
                error={error}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="como-funciona">
        <HowItWorks />
      </section>

      {/* Trust Section */}
      <section id="seguranca">
        <TrustSection />
      </section>

      {/* Conversion Section */}
      <ConversionSection />

      {/* Footer */}
      <Footer />
    </>
  );
};

export default Home;
